/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { ... } from '/ezlibrary/helpers/EzHelpers.js'
        import { EzApi } from '/public/common/javascript/ezapi.js';
        import { EzUI } from '/public/common/javascript/ezui.js';
        import { ezUI } from '/public/common/javascript/ezui.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends {EzStaticClass}
 * Provides static constants, properties, and functions related to characters.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *     import {
 *         // ... other helper classes ...,
 *         EzChar
 *     } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes (that allow it) with:
 *     import { EzChar } from '/ezlibrary/helpers/EzChar.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access to fields, properties, and functions only:
 *     EzClass.{field, property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzChar extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets an empty string/character constant
     * @returns {string}
     */
    static get EMPTY() {
        return '';
    };

    /**
     * @static @field
     * Stores an array of ambiguous characters (group, slashe, and bar characters).
     * @type {array<string>}
     */
    static #ambiguousCharacters = [
        '{',
        '}',
        '[',
        ']',
        '(',
        ')',
        '/',
        '\\',
        '|',
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of ambiguous characters (group, slashe, and bar characters)
     * @returns {array}
     */
    static get ambiguousCharacters() {
        return this.#ambiguousCharacters;
    }

    /**
     * @static @field
     * Stores an array of symbols (excluding quote, grouping, slashe, and bar characters).
     * @type {array<string>}
     */
    static #symbols = [
        '@',
        '#',
        '$',
        '%',
        '^',
        '&',
        '*',
        '-',
        '=',
        '+',
        '>',
        '<',
        '~',
        '_',
        '<',
        '>'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of symbols (excluding quote, grouping, slashe, and bar characters)
     * @returns {array}
     */
    static get symbols() {
        return this.#symbols;
    }

    /**
     * @static @field
     * Stores an array of math related characters (%, *, -, =, +, >, <, and //).
     * @type {array<string>}
     */
    static #mathCharacters = [
        '%',
        '*',
        '-',
        '=',
        '+',
        '>',
        '<',
        '//',
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of math related characters
     * @returns {array}
     */
    static get mathCharacters() {
        return this.#mathCharacters;
    }

    /**
     * @static @field
     * Stores an array of quote characters (backtick, single quote, and double quote).
     * @type {array<string>}
     */
    static #quotes = [
        '`',
        '\'',
        '"'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of quote characters
     * @returns {array}
     */
    static get quotes() {
        return this.#quotes;
    }

    /**
     * @static @field
     * Stores an array of punctuation symbols (!, :, ;, ., ?, and ,).
     * @type {array<string>}
     */
    static #punctuation = [
        '!',
        ':',
        ';',
        '.',
        '?',
        ','
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array punctuation symbols
     * @returns {array}
     */
    static get punctuation() {
        return this.#punctuation;
    }

    /**
     * @static @field
     * Stores an array of visually similar characters (0, o, O, 1, l, I).
     * @type {array<string>}
     */
    static #similarCharacters = [
        '0',
        'o',
        'O',
        '1',
        'l',
        'I'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of visually similar characters (0, o, O, 1, l, I)
     * @returns {array}
     */
    static get similarCharacters() {
        return this.#similarCharacters;
    }

    /**
     * @static @field
     * Stores an array of single digit numbers characters ('0' through '9').
     * @type {array<string>}
     */
    static #numbers = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array single digit numbers (0 through 9)
     * @returns {array}
     */
    static get numbers() {
        return this.#numbers;
    }

    /**
     * @static @field
     * Stores an array of upper case letters (A through Z).
     * @type {array}
     */
    static #uppercaseLetters = [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of upper case letters (A through Z).
     * @returns {array}
     */
    static get uppercaseLetters() {
        return this.#uppercaseLetters;
    }

    /**
     * @static @field
     * Stores an array of lower case letters (a through z).
     * @type {array}
     */
    static #lowercaseLetters = [
        'a',
        'b',
        'c',
        'd',
        'e',
        'f',
        'g',
        'h',
        'i',
        'j',
        'k',
        'l',
        'm',
        'n',
        'o',
        'p',
        'q',
        'r',
        's',
        't',
        'u',
        'v',
        'w',
        'x',
        'y',
        'z'
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of lower case letters (a through z).
     * @returns {array}
     */
    static get lowercaseLetters() {
        return this.#lowercaseLetters;
    }

    /**
     * @static
     * @private @field
     * Stores an array of upper case and lower case letters (A-Z and a-z).
     * @type {array}
     */
    static #letters = [
        ...this.uppercaseLetters,
        ...this.lowercaseLetters
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of upper case and lower case letters (A-Z and a-z).
     * @returns {array}
     */
    static get letters() {
        return this.#letters;
    }

    /**
     * @private @field
     * Stores an array of all characters defined in this class (ambiguous characters, symbols, math characters, quotes, punctuation, similar characters,
     * numbers, uppercase letters, and lowercase letters).
     * @type {array}
     */
    static #allCharacters = [
        ...EzChar.ambiguousCharacters,
        ...EzChar.symbols,
        ...EzChar.mathCharacters,
        ...EzChar.quotes,
        ...EzChar.punctuation,
        ...EzChar.similarCharacters,
        ...EzChar.numbers,
        ...EzChar.uppercaseLetters,
        ...EzChar.lowercaseLetters
    ];
    /**
     * @static
     * @public @readonly @property
     * Gets an array of all characters defined in this class (ambiguous characters, symbols, math characters, quotes, punctuation, similar characters,
     * numbers, uppercase letters, and lowercase letters).
     * @returns {array}
     */
    static get characters() {
        return this.#allCharacters;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'A'.
     * @returns {string}
     */
    static get A() {
        return 'A';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'B'.
     * @returns {string}
     */
    static get B() {
        return 'B';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'C'.
     * @returns {string}
     */
    static get C() {
        return 'C';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'D'.
     * @returns {string}
     */
    static get D() {
        return 'D';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'E'.
     * @returns {string}
     */
    static get E() {
        return 'E';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'F'.
     * @returns {string}
     */
    static get F() {
        return 'F';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'G'.
     * @returns {string}
     */
    static get G() {
        return 'G';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'H'.
     * @returns {string}
     */
    static get H() {
        return 'H';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'I'.
     * @returns {string}
     */
    static get I() {
        return 'I';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'J'.
     * @returns {string}
     */
    static get J() {
        return 'J';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'K'.
     * @returns {string}
     */
    static get K() {
        return 'K';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'L'.
     * @returns {string}
     */
    static get L() {
        return 'L';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'M'.
     * @returns {string}
     */
    static get M() {
        return 'M';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'N'.
     * @returns {string}
     */
    static get N() {
        return 'N';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'O'.
     * @returns {string}
     */
    static get O() {
        return 'O';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'P'.
     * @returns {string}
     */
    static get P() {
        return 'P';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'Q'.
     * @returns {string}
     */
    static get Q() {
        return 'Q';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'R'.
     * @returns {string}
     */
    static get R() {
        return 'R';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'S'.
     * @returns {string}
     */
    static get S() {
        return 'S';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'T'.
     * @returns {string}
     */
    static get T() {
        return 'T';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'U'.
     * @returns {string}
     */
    static get U() {
        return 'U';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'V'.
     * @returns {string}
     */
    static get V() {
        return 'V';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'W'.
     * @returns {string}
     */
    static get W() {
        return 'W';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'X'.
     * @returns {string}
     */
    static get X() {
        return 'X';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'Y'.
     * @returns {string}
     */
    static get Y() {
        return 'Y';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the uppercase letter character constant 'Z'.
     * @returns {string}
     */
    static get Z() {
        return 'Z';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'a'.
     * @returns {string}
     */
    static get a() {
        return 'a';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'b'.
     * @returns {string}
     */
    static get b() {
        return 'b';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'c'.
     * @returns {string}
     */
    static get c() {
        return 'c';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'd'.
     * @returns {string}
     */
    static get d() {
        return 'd';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'e'.
     * @returns {string}
     */
    static get e() {
        return 'e';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'f'.
     * @returns {string}
     */
    static get f() {
        return 'f';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'g'.
     * @returns {string}
     */
    static get g() {
        return 'g';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'h'.
     * @returns {string}
     */
    static get h() {
        return 'h';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'i'.
     * @returns {string}
     */
    static get i() {
        return 'i';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'j'.
     * @returns {string}
     */
    static get j() {
        return 'j';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'k'.
     * @returns {string}
     */
    static get k() {
        return 'k';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'l'.
     * @returns {string}
     */
    static get l() {
        return 'l';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'm'.
     * @returns {string}
     */
    static get m() {
        return 'm';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'n'.
     * @returns {string}
     */
    static get n() {
        return 'n';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'o'.
     * @returns {string}
     */
    static get o() {
        return 'o';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'p'.
     * @returns {string}
     */
    static get p() {
        return 'p';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'q'.
     * @returns {string}
     */
    static get q() {
        return 'q';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'r'.
     * @returns {string}
     */
    static get r() {
        return 'r';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 's'.
     * @returns {string}
     */
    static get s() {
        return 's';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 't'.
     * @returns {string}
     */
    static get t() {
        return 't';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'u'.
     * @returns {string}
     */
    static get u() {
        return 'u';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'v'.
     * @returns {string}
     */
    static get v() {
        return 'v';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'w'.
     * @returns {string}
     */
    static get w() {
        return 'w';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'x'.
     * @returns {string}
     */
    static get x() {
        return 'x';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'y'.
     * @returns {string}
     */
    static get y() {
        return 'y';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the lowercase letter character constant 'z'.
     * @returns {string}
     */
    static get z() {
        return 'z';
    }

    /**
     * @public @static @readonly @property
     * Gets the HTML character for a non-breaking space.
     * @returns {string}
     */
    static get nbsp() {
        return '&nbsp;';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the space character constant (' ').
     * @returns {string}
     */
    static get space() {
        return ' ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the underscore character constant ('_').
     * @returns {string}
     */
    static get underscore() {
        return '_';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the dash/hyphen character constant ('-').
     * @returns {string}
     */
    static get dash() {
        return '-';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the period/full stop character constant ('.').
     * @returns {string}
     */
    static get period() {
        return '.';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the dot character constant ('.'). Alias for {@link EzChar.period}.
     * @returns {string}
     */
    static get dot() {
        return EzChar.period;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the comma character constant (',').
     * @returns {string}
     */
    static get comma() {
        return ',';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the semicolon character constant (';').
     * @returns {string}
     */
    static get semicolon() {
        return ';';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the colon character constant (':').
     * @returns {string}
     */
    static get colon() {
        return ':';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the exclamation mark character constant ('!').
     * @returns {string}
     */
    static get exclamation() {
        return '!';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the question mark character constant ('?').
     * @returns {string}
     */
    static get question() {
        return '?';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the apostrophe/single quote character constant ("'").
     * @returns {string}
     */
    static get apostrophe() {
        return '\'';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the single quote character constant ("'"). Alias for {@link EzChar.apostrophe}.
     * @returns {string}
     */
    static get quote() {
        return EzChar.apostrophe;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the double quote character constant ('"').
     * @returns {string}
     */
    static get double_quote() {
        return '"';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the at sign character constant ('@').
     * @returns {string}
     */
    static get at() {
        return '@';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the hash/number sign character constant ('#').
     * @returns {string}
     */
    static get hash() {
        return '#';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the dollar sign character constant ('$').
     * @returns {string}
     */
    static get dollar() {
        return '$';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the percent sign character constant ('%').
     * @returns {string}
     */
    static get percent() {
        return '%';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the caret character constant ('^').
     * @returns {string}
     */
    static get caret() {
        return '^';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ampersand character constant ('&').
     * @returns {string}
     */
    static get ampersand() {
        return '&';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the ampersand character constant ('&'). Alias for {@link EzChar.ampersand}.
     * @returns {string}
     */
    static get and() {
        return '&';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the asterisk character constant ('*').
     * @returns {string}
     */
    static get asterisk() {
        return '*';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the star/asterisk character constant ('*'). Alias for {@link EzChar.asterisk}.
     * @returns {string}
     */
    static get star() {
        return '*';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the left parenthesis character constant ('(').
     * @returns {string}
     */
    static get left_parenthesis() {
        return '(';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the right parenthesis character constant (')').
     * @returns {string}
     */
    static get right_parenthesis() {
        return ')';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the plus sign character constant ('+').
     * @returns {string}
     */
    static get plus() {
        return '+';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the equals sign character constant ('=').
     * @returns {string}
     */
    static get equals() {
        return '=';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the left square bracket character constant ('[').
     * @returns {string}
     */
    static get left_bracket() {
        return '[';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the right square bracket character constant (']').
     * @returns {string}
     */
    static get right_bracket() {
        return ']';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the left curly brace character constant ('{').
     * @returns {string}
     */
    static get left_brace() {
        return '{';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the right curly brace character constant ('}').
     * @returns {string}
     */
    static get right_brace() {
        return '}';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the left angle bracket/less-than character constant ('<').
     * @returns {string}
     */
    static get left_angle_bracket() {
        return '<';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the right angle bracket/greater-than character constant ('>').
     * @returns {string}
     */
    static get right_angle_bracket() {
        return '>';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the backslash character constant ('\\').
     * @returns {string}
     */
    static get backslash() {
        return '\\';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the forward slash character constant ('/').
     * @returns {string}
     */
    static get forward_slash() {
        return '/';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the tilde character constant ('~').
     * @returns {string}
     */
    static get tilde() {
        return '~';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the backtick/grave accent character constant ('`').
     * @returns {string}
     */
    static get backtick() {
        return '`';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the vertical bar/pipe character constant ('|').
     * @returns {string}
     */
    static get bar() {
        return '|';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the null character constant ('\0').
     * @returns {string}
     */
    static get nullChar() {
        return '\0';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the line feed/newline character constant ('\n').
     * @returns {string}
     */
    static get line_feed() {
        return '\n';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the carriage return character constant ('\r').
     * @returns {string}
     */
    static get return() {
        return '\r';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the horizontal tab character constant ('\t').
     * @returns {string}
     */
    static get tab() {
        return '\t';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the form feed character constant ('\f').
     * @returns {string}
     */
    static get form_feed() {
        return '\f';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '0' character constant.
     * @returns {string}
     */
    static get zero() {
        return '0';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '1' character constant.
     * @returns {string}
     */
    static get one() {
        return '1';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '2' character constant.
     * @returns {string}
     */
    static get two() {
        return '2';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '3' character constant.
     * @returns {string}
     */
    static get three() {
        return '3';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '4' character constant.
     * @returns {string}
     */
    static get four() {
        return '4';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '5' character constant.
     * @returns {string}
     */
    static get five() {
        return '5';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '6' character constant.
     * @returns {string}
     */
    static get six() {
        return '6';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '7' character constant.
     * @returns {string}
     */
    static get seven() {
        return '7';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '8' character constant.
     * @returns {string}
     */
    static get eight() {
        return '8';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the digit '9' character constant.
     * @returns {string}
     */
    static get nine() {
        return '9';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the colon followed by a space character sequence constant (': ').
     * @returns {string}
     */
    static get colon_space() {
        return ': ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the space-equals-space character sequence constant (' = ').
     * @returns {string}
     */
    static get space_equal_space() {
        return ' = ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the space followed by an open bracket character sequence constant (' [').
     * @returns {string}
     */
    static get space_open_bracket() {
        return ' [';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the space followed by an open brace character sequence constant (' {').
     * @returns {string}
     */
    static get space_open_brace() {
        return ' {';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the space followed by an open parenthesis character sequence constant (' (').
     * @returns {string}
     */
    static get space_open_parenthesis() {
        return ' (';
    }
}

globalThis.EzChar = EzChar;